import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SelectOption } from '~/shared/utils/types';

import { apiFilterKey } from '../../../common/serachParamKeys';

interface ParsedFilters {
  active?: boolean[];
  ruleIds?: string[];
}

interface FilterState {
  filters: ParsedFilters;
  appliedRuleIDFilters: SelectOption[];
  appliedAutoscaleState: string[];
}

export function useAutoscaleRulesFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filterState: FilterState = useMemo(() => {
    const apiFilters = searchParams.get(apiFilterKey);

    const currentFilters = JSON.parse(apiFilters || '[]') as SelectOption[];
    const ruleIdFilters = currentFilters.filter((f) => f.group === 'ruleId');
    const activeStateFilters = currentFilters.filter((f) => f.group === 'activeRuleState');

    const parsedFilters: ParsedFilters = {};

    if (ruleIdFilters.length > 0) {
      parsedFilters.ruleIds = ruleIdFilters.map((f) => f.id);
    }

    if (activeStateFilters.length > 0 && activeStateFilters.some((f) => f.id === 'active')) {
      parsedFilters.active = [true];
    }

    return {
      filters: parsedFilters,
      appliedRuleIDFilters: ruleIdFilters,
      appliedAutoscaleState: parsedFilters.active ? ['active'] : [],
    };
  }, [searchParams]);

  const handleResetFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        updated.delete(apiFilterKey);
        return updated;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const handleApplyFilters = useCallback(
    (newFilters: ParsedFilters) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);

          // Build SelectOption array for rule IDs
          const ruleIdOptions: SelectOption[] =
            newFilters.ruleIds?.map((id) => ({
              id,
              name: id,
              group: 'ruleId' as const,
            })) || [];

          // Build SelectOption for active state
          const activeStateOptions: SelectOption[] =
            newFilters.active && newFilters.active.length > 0
              ? [{ id: 'active', name: 'Active', group: 'activeRuleState' as const }]
              : [];

          // Combine with existing filters (excluding ruleId and activeRuleState)
          const existingFilters = JSON.parse(prev.get(apiFilterKey) || '[]') as SelectOption[];
          const otherFilters = existingFilters.filter(
            (f) => f.group !== 'ruleId' && f.group !== 'activeRuleState'
          );
          const finalFilters = [...otherFilters, ...ruleIdOptions, ...activeStateOptions];

          // Update api_filters parameter
          if (finalFilters.length > 0) {
            updated.set(apiFilterKey, JSON.stringify(finalFilters));
          } else {
            updated.delete(apiFilterKey);
          }

          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const apiFilters = searchParams.get(apiFilterKey);

  return {
    ...filterState,
    apiFilters,
    handleResetFilters,
    handleApplyFilters,
  };
}
